import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { filter, throttleTime } from 'rxjs/operators';
import * as SourceMappedStackTrace from 'sourcemapped-stacktrace';
import { HttpService, AppError, Browser, SsrService } from '@interticket/core';
import { slackErrorLogConfig } from '../../configs/error-handler/slack-error-logger.config';
import { stackdriverConfig } from '../../configs/error-handler/stackdriver.config';
import { ISlackAPIConfig } from '../../interfaces/error-handler/slack-error-logger-config.interface';
import { IStackdriverConfig } from '../../interfaces/error-handler/stackdriver-config.interface';
import { ISlackMessage } from '../../interfaces/error-handler/slack-message.interface';
import { ErrorService } from './error.service';
import { environment } from '@env';
import StackdriverErrorReporter from 'stackdriver-errors-js';

@Injectable()
export class ErrorLoggerService {

  // Slack
  private readonly slackConfig: ISlackAPIConfig = slackErrorLogConfig(environment);
  private readonly slackMessages$: BehaviorSubject<ISlackMessage[]> = new BehaviorSubject([]);
  private lastSlackMessage: string;

  // Stackdriver
  private readonly stackdriverConfig: IStackdriverConfig = stackdriverConfig(environment);
  private stackdriverReporter: StackdriverErrorReporter;

  constructor(
    private httpService: HttpService,
    private errorService: ErrorService,
    private ssrService: SsrService
  ) {
    this.subscribeToSlackLogging();
    this.setStackdriverReporter();
  }

  @Browser()
  private setStackdriverReporter(): void {
    this.stackdriverReporter = new StackdriverErrorReporter();
    this.stackdriverReporter.start(this.stackdriverConfig);
  }

  @Browser()
  private subscribeToSlackLogging(): void {
    if (this.slackConfig.logWebhookUrl) {
      this.slackMessages$
        .pipe(
          filter((slackMessages: ISlackMessage[]) => !!slackMessages?.length),
          throttleTime(1000)
        )
        .subscribe({
          next: (slackMessages: ISlackMessage[]) => {
            this.logToSlack(slackMessages);
            this.slackMessages$.next([]);
          },
        });
    }
  }

  @Browser()
  private logToSlack(slackMessages: ISlackMessage[]): void {
    const options = {
      headers: this.slackConfig.httpHeaders,
      responseType: 'text',
    };

    this.httpService.post<Response>(this.slackConfig.logWebhookUrl, { attachments: slackMessages }, options, { static: true })
      .subscribe();
  }

  @Browser()
  private createSlackMessage(message: string, stack = ''): void {
    if (this.slackConfig.logWebhookUrl) {
      const slackMessage: ISlackMessage = {
        pretext: this.slackConfig.pretext,
        title: message,
        text: stack,
        color: 'danger',
        author_name: this.ssrService.location.href,
        author_link: this.ssrService.location.href,
      };

      const stringifiedMessage = JSON.stringify(slackMessage);

      if (stringifiedMessage !== this.lastSlackMessage) {
        this.slackMessages$.next([...this.slackMessages$.getValue(), slackMessage]);
      }

      this.lastSlackMessage = stringifiedMessage;
    }
  }

  private logToConsole(message: string, stack: string): void {
    console.error(`%cErrorLoggerService\n`, 'font-weight: bold', `${message}\n`, stack);
  }

  @Browser()
  private logToStackdriver(error: AppError): void {
    if (error) {
      this.stackdriverReporter.report(error);
    }
  }

  @Browser()
  private getMappedStackTrace(stack: string[]): string {
    const stackTrace: string[] = Array.isArray(stack) ? stack : [stack];
    return stackTrace.join('\n');
  }

  @Browser()
  logError(error: AppError): void {
    const message: string = this.errorService.getErrorMessage(error).toString();
    const stack: string = error instanceof HttpErrorResponse ? '' : (error as Error)?.stack;

    SourceMappedStackTrace.mapStackTrace(stack, (mappedStack: string[]) => {
      const stackTrace: string = this.getMappedStackTrace(mappedStack);

      this.logToConsole(message, stackTrace);
      this.logToStackdriver(stackTrace && error);
      this.createSlackMessage(message, stackTrace);
    });
  }

}
